import { IconFromSet } from "@cmp-src/icons";

import { FeatureItem } from "./FeatureItem";
import "./feature-announcement.css";
import type { SlideCardProps } from "./types";
import { Button } from "@cmp-src/primitives/button";
import React from "react";

const getCardClassName = (
  animationState: "active" | "exiting" | "entering",
  direction: "left" | "right"
): string => {
  const base = "slide-card-base";

  if (animationState === "active") {
    return `${base} slide-card-active`;
  }

  if (animationState === "exiting") {
    return `${base} slide-card-exiting-${direction}`;
  }

  if (animationState === "entering") {
    return `${base} slide-card-entering-${direction}`;
  }

  return base;
};

export const SlideCard: React.FC<SlideCardProps> = ({
  slide,
  theme,
  currentSlideIndex,
  totalSlides,
  onClose,
  onTryAction,
  onMaybeLater,
  onIndicatorClick,
  animationState,
  direction
}) => {
  // Exiting card should be absolute (doesn't affect height), entering card should be relative (defines new height)
  // This ensures the container height smoothly transitions to match the new card
  const shouldBeAbsolute = animationState === "exiting";
  // Entering card should be on top visually for the slide-in effect
  const zIndex = animationState === "entering" ? "z-10" : "";

  return (
    <div
      className={`${shouldBeAbsolute ? "absolute top-0 right-0 left-0" : "relative"} ${zIndex} ${getCardClassName(animationState, direction)} overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-2xl backdrop-blur-xl`}
    >
      {/* Gradient accent */}
      <div className={`absolute top-0 right-0 left-0 h-1 ${theme.accent}`} />

      {/* Header */}
      <div className="p-6 pb-4 text-center">
        <div className="mb-4 flex justify-center">
          <div className="relative">
            <div
              className={`absolute inset-0 ${theme.iconGlow} rounded-2xl opacity-50 blur-xl`}
            />
            <div
              className={`relative flex h-16 w-16 items-center justify-center rounded-2xl shadow-lg ${theme.iconBg}`}
            >
              <IconFromSet icon={slide.icon} size={8} color="white" />
            </div>
          </div>
        </div>

        <span
          className={`mb-3 inline-block rounded-full border px-3 py-1 font-medium text-xs ${theme.badgeText} ${theme.badgeBg} ${theme.badgeBorder}`}
        >
          {slide.badge}
        </span>

        <h2 className="mb-2 font-bold text-2xl text-[#0F2449]">
          {slide.title}
        </h2>
        <p className="text-neutral-600 text-sm leading-relaxed">
          {slide.description}
        </p>
      </div>

      {/* Features */}
      <div className="px-6 pb-4">
        <div className="space-y-3">
          {slide.features.map((feature) => (
            <FeatureItem
              key={feature.title}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
              theme={theme}
            />
          ))}
        </div>
      </div>

      {/* Keyboard shortcut */}
      {slide.shortcut && (
        <div className="px-6 pb-4">
          <div className="flex items-center justify-center gap-2 text-neutral-500 text-xs">
            <span>Press</span>
            {slide.shortcut.keys.map((key, index) => (
              <span key={key} className="flex items-center gap-2">
                <kbd className="rounded border border-neutral-200 bg-neutral-100 px-2 py-1 font-mono text-neutral-600">
                  {key}
                </kbd>
                {index < slide.shortcut!.keys.length - 1 && <span>+</span>}
              </span>
            ))}
            <span>{slide.shortcut.description}</span>
          </div>
        </div>
      )}

      {/* Slide Indicators */}
      {totalSlides > 1 && (
        <div className="flex justify-center gap-2 pb-4">
          {Array.from({ length: totalSlides }).map((_, index) => (
            <button
              key={`slide-indicator-${index}`}
              type="button"
              onClick={() => onIndicatorClick(index)}
              className={`h-2 rounded-full ${
                index === currentSlideIndex
                  ? "w-6 bg-[#0F2449]"
                  : "w-2 bg-neutral-300 hover:bg-neutral-400"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* CTA */}
      <div className="flex flex-col gap-3 px-6 pb-6">
        <Button
          onClick={onTryAction}
          className={`w-full bg-gradient-to-r py-6 font-medium text-white shadow-lg transition-all ${theme.ctaGradient} ${theme.ctaHover} ${theme.ctaShadow} ${theme.ctaShadowHover}`}
        >
          {slide.ctaText}
        </Button>
        <Button className={"py-5"} onClick={onMaybeLater} variant={"outline"}>
          Maybe Later
        </Button>
      </div>
    </div>
  );
};
